import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star, Users, Settings2, MapPin, Zap, Heart, GitCompare } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { useState } from "react";
import { toast } from "sonner";
import { useCompare } from "../contexts/CompareContext";
import UsdHint from "./UsdHint";

export default function CarCard({ vehicle, distanceKm }) {
  const { t } = useTranslation();
  const [liked, setLiked] = useState(false);
  const { toggle: toggleCompare, has: isInCompare, max: compareMax } = useCompare();
  const inCompare = isInCompare(vehicle.vehicle_id);

  const {
    vehicle_id,
    make,
    model,
    year,
    photos,
    daily_rate,
    rating,
    review_count,
    seats,
    transmission,
    location_city,
    instant_book,
    unlimited_mileage,
    delivery_enabled,
  } = vehicle;

  const distLabel =
    typeof distanceKm === "number" && isFinite(distanceKm)
      ? distanceKm < 1
        ? `${Math.round(distanceKm * 1000)} m`
        : `${distanceKm.toFixed(1)} km`
      : null;

  return (
    <Link
      to={`/cars/${vehicle_id}`}
      className="car-card block group"
      data-testid={`car-card-${vehicle_id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-3">
        <img
          src={photos?.[0] || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800"}
          alt={`${make} ${model}`}
          className="car-image w-full h-full object-cover"
        />

        {/* Like + Compare buttons */}
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              setLiked(!liked);
            }}
            className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:scale-110 transition-transform"
            aria-label={t("carCard.favorite")}
          >
            <Heart className={`w-4 h-4 ${liked ? "fill-red-500 text-red-500" : "text-neutral-600"}`} />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              const res = toggleCompare(vehicle);
              if (!res.ok && res.reason === "max") {
                toast.info(t("carCard.compareMax", { max: compareMax }));
              } else if (res.ok) {
                toast.success(inCompare ? t("carCard.removedCompare") : t("carCard.addedCompare"));
              }
            }}
            className={`w-8 h-8 backdrop-blur-sm rounded-full flex items-center justify-center hover:scale-110 transition-transform ${
              inCompare ? "bg-amber-400 text-amber-950" : "bg-white/90 text-neutral-600"
            }`}
            aria-label={inCompare ? t("carCard.removeCompare") : t("carCard.addCompare")}
            title={t("carCard.compare")}
            data-testid={`compare-toggle-${vehicle.vehicle_id}`}
          >
            <GitCompare className="w-4 h-4" />
          </button>
        </div>

        {/* Badges */}
        <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
          {rating >= 4.8 && review_count >= 3 && (
            <Badge className="bg-amber-400 text-amber-950 text-xs border-0 font-semibold" data-testid="premium-badge">
              <Star className="w-3 h-3 mr-1 fill-current" />
              Premium
            </Badge>
          )}
          {instant_book && (
            <Badge className="bg-[#111111] text-white text-xs border-0">
              <Zap className="w-3 h-3 mr-1" />
              Instant
            </Badge>
          )}
          {delivery_enabled && (
            <Badge className="bg-white/90 backdrop-blur-sm text-[#111111] text-xs border-0">
              {t("carCard.delivery")}
            </Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <div>
        {/* Title & Rating */}
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-heading font-semibold text-[15px] text-[#111111]">
            {make} {model} {year}
          </h3>
          {review_count > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 fill-[#111111] text-[#111111]" />
              <span className="text-sm font-medium">{rating?.toFixed(1)}</span>
              <span className="text-neutral-400 text-sm">({review_count})</span>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="flex items-center gap-3 text-neutral-500 text-sm mb-2">
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {seats}
          </span>
          <span className="flex items-center gap-1">
            <Settings2 className="w-3.5 h-3.5" />
            {transmission === "automatic" ? t("carCard.auto") : t("carCard.manual")}
          </span>
          {unlimited_mileage && (
            <span className="text-emerald-600 font-medium">{t("carCard.unlimitedKm")}</span>
          )}
        </div>

        {/* Location */}
        <div className="flex items-center gap-1 text-neutral-400 text-sm mb-2">
          <MapPin className="w-3.5 h-3.5" />
          <span>{location_city}</span>
          {distLabel && (
            <span
              className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 text-[11px] font-semibold"
              data-testid={`car-distance-${vehicle_id}`}
            >
              <Zap className="w-3 h-3" />
              {distLabel}
            </span>
          )}
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-1">
          <span className="font-heading font-bold text-lg">${daily_rate?.toLocaleString()}</span>
          <span className="text-neutral-500 text-sm">{t("carCard.perDay")}</span>
          <UsdHint amountMxn={daily_rate} className="ml-1" />
        </div>
      </div>
    </Link>
  );
}
